import { formatCurrency } from "@solomon-apps/shared";

const ONE_DAY_MS = 86_400_000;
const PROJECTION_DAYS = 365;

export interface EarningsPoint {
    timestamp: number;
    user: number;
    bench: number;
}

export interface EarningsSeries {
    points: EarningsPoint[];
    /** Timestamp at which the forward projection starts. */
    startTimestamp: number | null;
    /** Whole days spanned by the projection. */
    days: number;
}

/**
 * Daily growth factor implied by an APY.
 *
 * An APY is already annualized *and* compounded, so the honest daily
 * step is the 365th root of the annual factor — not `apy / 365`, which
 * would under-report by the compounding it already contains.
 */
function dailyGrowthFactor(apyPct: number): number {
    return Math.pow(1 + apyPct / 100, 1 / 365);
}

/**
 * Projects one year at constant rates. Published APYs use daily growth;
 * a paid historical APR uses simple interest without reinvesting rewards.
 * The benchmark remains an APY.
 *
 * Both series start at 0 on the window's first day — the chart plots
 * *earnings*, not balance, so the two lines share an origin and the
 * gap between them reads directly as the advantage.
 *
 * Plain `number` math is intentional: this is an illustrative
 * comparison, not token math that has to round-trip on chain.
 */
export function buildProjectedEarningsSeries(
    principal: number,
    usdvApyPct: number,
    benchApyPct: number,
    startTimestamp: number,
    usdvRateType: "apy" | "apr" = "apy"
): EarningsSeries {
    if (
        !Number.isFinite(principal) ||
        !Number.isFinite(usdvApyPct) ||
        !Number.isFinite(benchApyPct) ||
        !Number.isFinite(startTimestamp) ||
        principal < 0 ||
        usdvApyPct <= -100 ||
        benchApyPct <= -100
    ) {
        return { points: [], startTimestamp: null, days: 0 };
    }

    let userBalance = principal;
    let benchBalance = principal;
    const points: EarningsPoint[] = [{ timestamp: startTimestamp, user: 0, bench: 0 }];

    const usdvGrowthFactor = dailyGrowthFactor(usdvApyPct);
    const benchGrowthFactor = dailyGrowthFactor(benchApyPct);
    for (let day = 1; day <= PROJECTION_DAYS; day += 1) {
        if (usdvRateType === "apy") userBalance *= usdvGrowthFactor;
        benchBalance *= benchGrowthFactor;

        points.push({
            timestamp: startTimestamp + day * ONE_DAY_MS,
            user:
                usdvRateType === "apr"
                    ? (principal * (usdvApyPct / 100) * day) / PROJECTION_DAYS
                    : userBalance - principal,
            bench: benchBalance - principal
        });
    }

    return {
        points,
        startTimestamp,
        days: PROJECTION_DAYS
    };
}

/** Below this, whole dollars round the figure away entirely — a $3.88
 *  balance earns cents, and "$0" is not an answer. */
const CENTS_BELOW = 10;

/** Decimal places `formatEarnings` renders at for this magnitude. */
function displayDecimals(amount: number): number {
    return Math.abs(amount) < CENTS_BELOW ? 2 : 0;
}

/**
 * Dollar figures for the comparison panel: cents under $10, whole
 * dollars at or above it. Keeps a small balance's earnings legible
 * without dragging ".00" through every larger figure.
 */
export function formatEarnings(amount: number): string {
    return displayDecimals(amount) === 2 ? formatCurrency(amount, 2, 2) : formatCurrency(amount, 0, 0);
}

/**
 * Snaps an amount to the precision it will be shown at.
 *
 * Figures that are displayed together have to reconcile on screen.
 * Subtracting the raw values and rounding the result independently
 * lets the rounding errors diverge: $0.5240 and $0.0155 render as
 * "$0.52" and "$0.02", but their raw difference rounds to "$0.51" —
 * a gap the reader can't reproduce from the numbers in front of them.
 * Rounding the operands first makes the arithmetic on screen true.
 */
export function toDisplayPrecision(amount: number): number {
    const factor = 10 ** displayDecimals(amount);
    return Math.round(amount * factor) / factor;
}
